import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useCopyToClipboard} from 'common/hooks/useCopyToClipboard';
import {EmailIcon, Layout, Table, Text, Tooltip} from '@phonectas/unifon-ui-kit';
import {LIMIT_PAYLOAD} from 'common/constants';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {StyledSectionDiv} from '../../styled';
import {CustomerContact} from 'common/types/store/threeSixtyTypes';
import {phoneNumberWithoutSpaces} from 'utils/number';

interface ContactsTableProps {
	id: string;
}

const ContactsTable: React.FC<ContactsTableProps> = ({id}) => {
	const [t] = useTranslation();
	const [copyToClipboard] = useCopyToClipboard();
	const {getContacts} = useThreeSixtyNewStore((state) => ({getContacts: state.getContacts}), shallow);
	const {contacts, totalCount, accountNotFound} = useThreeSixtyNewStore((state) => ({
		contacts: state.accountTables?.contacts,
		totalCount: state.accountTables?.contacts?.total_count,
		accountNotFound: state.accountNotFound,

	}), shallow);
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
	const contactsData: CustomerContact[] = contacts?.customer_contacts || [];
	const contactsDate = contacts?.customer_contacts;
	const handleCopyEmail = useCallback((
		e: React.MouseEvent<HTMLSpanElement>,
		mail: string,
	) => {
		e.stopPropagation();
		copyToClipboard(mail);
	}, [copyToClipboard]);

	const onLoadMore = useCallback((size: number) => {
		if (!totalCount) return;
		if (totalCount <= size) return;
		getContacts({
			limit: LIMIT_PAYLOAD + size,
			id: id,
		});
	}, [id, totalCount, getContacts]);

	const renderItem = useCallback((item: CustomerContact, index: number) => {
		const isSelected = selectedContactId === item.id;
		return (
			<Table.Row
				key={index}
				index={index}
				style={{padding: '0px'}}
				selected={isSelected}
				onClick={() => setSelectedContactId(item.id)}
				expandedContent={
					<>
						{
							item?.note && (
								<Layout direction="column" fullWidth gap="xs">
									<Layout direction="row" gap="xs">
										<Text>{`${t('Note')}:`}</Text>
										<Text>{item.note}</Text>
									</Layout>
								</Layout>
							)
						}
					</>
				}
			>
				<Table.Cell>{item?.company_name}</Table.Cell>
				<Table.Cell>{item?.name}</Table.Cell>
				<Table.Cell>{phoneNumberWithoutSpaces(item?.phone_number)}</Table.Cell>
				<Table.Cell>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							cursor: 'pointer',
						}}
					>
						<Tooltip
							text={item?.email ? item?.email : ''}
						>
							<span
								style={{cursor: 'pointer'}}
								onClick={(e) => handleCopyEmail(e, item?.email)}
							>
								<EmailIcon />
							</span>
						</Tooltip>
					</span>
				</Table.Cell>
				<Table.Cell>{item?.address}</Table.Cell>
				<Table.Cell>{item?.type}</Table.Cell>
				<Table.Cell>{item?.note ? 'note' : ''}</Table.Cell>
			</Table.Row>
		);
	}, [handleCopyEmail, t, selectedContactId]);

	useEffect(() => {
		if (accountNotFound) return;
		if (totalCount === 0 && contacts?.customer_contacts === null && contacts?.filters?.account_id === id) return;

		const isAccountIdMatched =
			Array.isArray(contactsDate) &&
			contactsDate.length > 0 &&
			id === contactsDate[0]?.account_id;

		const isContactFetched = contacts !== null && isAccountIdMatched;
		const isContactsFilterMatched = contacts?.filters?.account_id === id && contactsDate === null;

		if (isContactFetched || isContactsFilterMatched) return;

		getContacts({
			id: id,
			limit: LIMIT_PAYLOAD,
		});
	}, [id, contacts, contactsDate, accountNotFound, getContacts, totalCount]);

	if (!contacts || !contacts?.customer_contacts) {
		return (
			<StyledSectionDiv $endDiv $fullHeight>
				<Layout
					fullHeight
					fullWidth
					alignItems="center"
					justifyContent="center"
				>
					<Text>{t('No data')}</Text>
				</Layout>
			</StyledSectionDiv>
		);
	}

	return (
		<StyledSectionDiv
			style={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: '1',
				overflow: 'hidden',
				padding: '16px 8px'
			}}
			$endDiv
			$fullHeight
		>
			<Table
				items={contactsData || []}
				renderItem={(item, index) => renderItem(item, index)}
				expandByClick
				virtualized
				onScrollDown={onLoadMore}
			>
				<Table.Row style={{padding: '0px'}}>
					<Table.Cell>{t('Company')}</Table.Cell>
					<Table.Cell>{t('Name')}</Table.Cell>
					<Table.Cell>{t('Phone')}</Table.Cell>
					<Table.Cell width={45}>{t('Email')}</Table.Cell>
					<Table.Cell>{t('Address')}</Table.Cell>
					<Table.Cell>{t('Type')}</Table.Cell>
					<Table.Cell>{t('Note')}</Table.Cell>
				</Table.Row>
			</Table>
		</StyledSectionDiv>
	);
}

export default React.memo(ContactsTable);
